/**
 * The payment schedule on the estimate builder.
 *
 * Every rule here comes from `paymentSchedule()` in @roof-hawk/core (ADR 0031):
 * the 50/50 default, the $1,000 threshold, and the Final payment as the
 * remainder. This file is the box the rep types in — it does no arithmetic of
 * its own, so the number the customer signs and the number the builder shows
 * cannot disagree.
 *
 * The Final recalculates on every keystroke rather than on blur, because a
 * schedule that only settles once you click away is a schedule people misread
 * mid-edit.
 */

import {
  cents,
  financingBlock,
  financingTermsGapFor,
  formatCents,
  paymentSchedule,
  toCents,
  FINAL_SLOT,
  FINANCING_MAX_TERM_MONTHS,
  PAYMENT_SLOT_LABEL,
  planProblem,
  planWording,
  showFinancing,
  type Cents,
  type EstimatePayment,
  type FinancingTerms,
} from "@roof-hawk/core";
import { useState } from "react";

import { t } from "../../i18n";

/** An amount as typed. Empty means "unanswered" — the default comes back. */
type Draft = Record<number, string>;

/**
 * The wording beside an amount. Its own input rather than the shared TextCell,
 * which lives in EstimateCard and would make the two files import each other.
 */
function TriggerCell({
  value,
  onSave,
}: {
  value: string;
  onSave: (value: string) => void;
}) {
  const [local, setLocal] = useState<string | null>(null);
  return (
    <input
      aria-label={t("Trigger")}
      value={local ?? value}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={() => {
        if (local === null) return;
        const typed = local;
        setLocal(null);
        if (typed !== value) onSave(typed);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
      }}
    />
  );
}

/**
 * What may be typed into a money box: digits, thousands separators, a currency
 * sign, and at most two decimal places.
 *
 * Filtering at the keystroke rather than parsing leniently afterwards is
 * deliberate. `Number.parseFloat("12abc")` is 12 and `parseFloat("abc")` is
 * NaN — the first silently invents an amount and the second silently resets the
 * slot to its 50% default. Neither is a thing to do to a number somebody is
 * about to read out loud to a customer.
 */
const MONEYISH = /^\$?[\d,]*(\.\d{0,2})?$/;

/** Dollars, or null for "cleared". Only ever called on a MONEYISH string. */
const parseDraft = (raw: string): number | null => {
  const cleaned = raw.replace(/[$,\s]/g, "");
  if (cleaned === "" || cleaned === ".") return null;
  const parsed = Number.parseFloat(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * A number typed into one of the three financing boxes.
 *
 * Empty is a real answer and it is not zero: it means "use whatever the
 * company is set to", which is what the column's NULL means. So an empty box
 * saves null and a `0` saves zero, and the two are never confused — typing 0%
 * for an interest-free promotion has to survive.
 */
function parseTermDraft(raw: string): number | null {
  const cleaned = raw.replace(/[$,%\s]/g, "");
  if (cleaned === "") return null;
  // An invalid draft must stay invalid. Treating it as null would clear the
  // field, and parseFloat would silently save the prefix of "12months".
  return Number(cleaned);
}

/** One box, held as a draft until it blurs, like every other box on this row. */
function TermCell({
  label,
  value,
  placeholder,
  onSave,
  valid,
}: {
  label: string;
  value: number | null;
  placeholder: string;
  onSave: (value: number | null) => void;
  /** What the database will accept. A refused number stays on screen. */
  valid: (value: number) => boolean;
}) {
  const [local, setLocal] = useState<string | null>(null);
  const shown = local ?? (value == null ? "" : String(value));
  const typed = local == null ? null : parseTermDraft(local);
  const refused = local != null && typed != null && !valid(typed);
  return (
    <>
      <input
        aria-label={t(label)}
        value={shown}
        inputMode="decimal"
        placeholder={placeholder}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={() => {
          if (local == null) return;
          const next = parseTermDraft(local);
          // A number the database would refuse is left in the box with the
          // reason under it, rather than saved and bounced or silently
          // dropped. The rep fixes it; nothing is written until they do.
          if (next != null && !valid(next)) return;
          setLocal(null);
          if (next === value) return;
          onSave(next);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") (e.target as HTMLInputElement).blur();
        }}
      />
      {refused ? (
        <div className="err" role="alert">
          {t("{label} is outside what the lender writes.", { label: t(label) })}
        </div>
      ) : null}
    </>
  );
}

/**
 * The down payment, the term and the rate.
 *
 * Jonathon, 2026-09-22: "there needs to be a down payment option on financing
 * where we can put an amount and it subtracts it from the total. we also need
 * to be able to enter in the term and rate so it can show customer their
 * payment."
 *
 * All three are optional and all three default to the company's programme,
 * which is why the placeholders show what will be used rather than sitting
 * empty: a rep needs to be able to see that this estimate is going out at
 * 11.9% over 120 months without typing either number.
 */
/**
 * What the customer would be quoted, with an empty box taking the company's
 * number. A term without a rate, or a rate without a term, is refused at
 * Present (ADR 0253), so it is said on the rows first, where it is fixed. A
 * half-filled default in Tools is named as the company's, because the
 * estimate's own boxes are empty and there is nothing on them to clear (#644).
 */
function termsGap(
  terms: { termMonths: number | null; apr: number | null } | undefined,
  companyTerms: { termMonths: number | null; apr: number | null } | undefined,
): string | null {
  const message = financingTermsGapFor(terms, companyTerms)?.message;
  return message == null ? null : t(message);
}

function FinancingTermsRows({
  terms,
  companyTerms,
  onSave,
  quoted,
}: {
  terms?:
    | {
        downPayment: number | null;
        termMonths: number | null;
        apr: number | null;
      }
    | undefined;
  companyTerms?: { termMonths: number | null; apr: number | null } | undefined;
  onSave: (
    patch: Partial<{
      financing_down_payment: number | null;
      financing_term_months: number | null;
      financing_apr: number | null;
    }>,
  ) => void;
  quoted: boolean;
}) {
  const gap = termsGap(terms, companyTerms);
  return (
    <>
      <div className="rrow">
        <span className="k">{t("Down payment")}</span>
        <TermCell
          label="Down payment"
          value={terms?.downPayment ?? null}
          placeholder={t("None")}
          valid={(v) => v >= 0}
          onSave={(v) => onSave({ financing_down_payment: v })}
        />
      </div>
      <div className="rrow">
        <span className="k">{t("Term (months)")}</span>
        <TermCell
          label="Term in months"
          value={terms?.termMonths ?? null}
          placeholder={
            companyTerms?.termMonths == null
              ? t("e.g. 120")
              : String(companyTerms.termMonths)
          }
          valid={(v) =>
            Number.isInteger(v) && v >= 1 && v <= FINANCING_MAX_TERM_MONTHS
          }
          onSave={(v) => onSave({ financing_term_months: v })}
        />
      </div>
      <div className="rrow">
        <span className="k">{t("Rate (APR %)")}</span>
        <TermCell
          label="Rate as an annual percentage"
          value={terms?.apr ?? null}
          placeholder={
            companyTerms?.apr == null
              ? t("e.g. 11.9")
              : String(companyTerms.apr)
          }
          valid={(v) => v >= 0 && v < 100}
          onSave={(v) => onSave({ financing_apr: v })}
        />
      </div>
      {gap ? (
        <div className="err" role="alert">
          {gap}
        </div>
      ) : null}
      <div className="n">
        {quoted
          ? t(
              "The payment above is what this loan actually costs, and it is what the customer’s copy prints. A down payment comes off the amount financed only — it does not change the contract total, the deposit or the payment schedule.",
            )
          : t(
              "Leave the term and the rate empty and the proposal shows the old “about $13 per $1,000” figure instead of a real payment. The company’s own programme is set in Tools → Integrations.",
            )}
      </div>
    </>
  );
}

/** What `PaymentSchedule` needs to show and save an in-house payment plan. */
export interface PlanProps {
  monthlyCents: number | null;
  firstDueOn: string | null;
  onSave: (
    patch: Partial<{
      plan_monthly_cents: number | null;
      plan_first_due_on: string | null;
    }>,
  ) => void;
}

/** A money box for the plan, saved in cents on blur. Empty saves null. */
function PlanMoneyCell({
  label,
  cents: value,
  onSave,
}: {
  label: string;
  cents: number | null;
  onSave: (cents: number | null) => void;
}) {
  const [local, setLocal] = useState<string | null>(null);
  const shown = local ?? (value == null ? "" : formatCents(cents(value)));
  return (
    <input
      className="num"
      aria-label={t(label)}
      value={shown}
      onFocus={() => setLocal(value == null ? "" : String(value / 100))}
      onChange={(e) => {
        if (!MONEYISH.test(e.target.value)) return;
        setLocal(e.target.value);
      }}
      onBlur={() => {
        if (local === null) return;
        const dollars = parseDraft(local);
        setLocal(null);
        const next = dollars == null ? null : toCents(dollars);
        if (next !== value) onSave(next);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
      }}
    />
  );
}

/**
 * An in-house payment plan (ADR 0286): the customer pays the down payment —
 * the First above — and then a monthly amount until the Final is paid off,
 * with no fee and no interest. Jonathon, 2026-10-14: "durward paid $500 up front and then
 * will mail us a check for $100 a month until he pays off his $2500 total."
 *
 * The sentence under the boxes is the one the proposal prints, from the same
 * function, so the rep reads what the customer will.
 */
function PaymentPlanRows({ plan, final }: { plan: PlanProps; final: Cents }) {
  const on = plan.monthlyCents != null;
  if (!on) {
    return (
      <div className="paysched">
        <div className="chk-sub">{t("Payment plan")}</div>
        <div className="rrow">
          <span className="n">
            {t("The customer pays us monthly instead of the Final in one go.")}
          </span>
          <button
            type="button"
            className="btn"
            onClick={() => {
              // A first guess the rep will change: $100 a month from the 1st
              // of next month, Durward's terms.
              const now = new Date();
              const next = new Date(now.getFullYear(), now.getMonth() + 1, 1);
              const day = `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, "0")}-01`;
              plan.onSave({
                plan_monthly_cents: 10_000,
                plan_first_due_on: day,
              });
            }}
          >
            {t("Set up a payment plan")}
          </button>
        </div>
      </div>
    );
  }

  const terms = {
    monthlyCents: cents(plan.monthlyCents ?? 0),
    firstDueOn: plan.firstDueOn ?? "",
  };
  const problem = planProblem(final, terms);
  const wording = planWording(final, terms);

  return (
    <div className="paysched">
      <div className="chk-sub">{t("Payment plan")}</div>
      <div className="rrow">
        <span className="k">{t("Each month")}</span>
        <PlanMoneyCell
          label="Monthly payment"
          cents={plan.monthlyCents}
          onSave={(value) => {
            // Emptying the box is not "no plan" — that is the button below.
            if (value == null || value <= 0) return;
            plan.onSave({ plan_monthly_cents: value });
          }}
        />
      </div>
      <div className="rrow">
        <span className="k">{t("First payment due")}</span>
        <input
          type="date"
          aria-label={t("First monthly payment due")}
          value={plan.firstDueOn ?? ""}
          onChange={(e) => {
            if (e.target.value)
              plan.onSave({ plan_first_due_on: e.target.value });
          }}
        />
      </div>
      {problem ? (
        <div className="err" role="alert">
          {t(problem)}
        </div>
      ) : (
        <div className="n" aria-label={t("Payment plan wording")}>
          {wording} {t("The customer reads this on the proposal.")}
        </div>
      )}
      <button
        type="button"
        className="btn"
        onClick={() =>
          plan.onSave({
            plan_monthly_cents: null,
            plan_first_due_on: null,
          })
        }
      >
        {t("Remove the payment plan")}
      </button>
    </div>
  );
}

export function PaymentSchedule({
  total,
  payments,
  onSaveAmount,
  onSaveTrigger,
  financing,
  plan,
}: {
  total: Cents;
  payments: readonly EstimatePayment[];
  /** Dollars, or null to clear the slot back to its default. */
  onSaveAmount: (paymentId: string, amount: number | null) => void;
  /** The wording beside the amount — "Due Upon Contract Signed". */
  onSaveTrigger: (paymentId: string, text: string | null) => void;
  /** The in-house payment plan, where this estimate can carry one. */
  plan?: PlanProps;
  financing?: {
    /**
     * Null is an estimate saved before the choice was forced. What happens to
     * one of those is decided by `companyDefault`, not by this file.
     */
    show: boolean | null;
    /** `company_settings.financing_offered` — off hides the row entirely. */
    companyOffers?: boolean | null;
    /** `company_settings.show_financing_default`. */
    companyDefault: boolean | null;
    /**
     * What kind of building this is. Commercial answers null with "hide" —
     * consumer credit is not what a property manager is buying (2026-08-27).
     */
    jobType?: string | null;
    onChange: (show: boolean) => void;
    /**
     * The terms typed on THIS estimate. Every one of them may be null, and
     * null means "whatever the company is set to".
     */
    terms?: {
      /** Dollars, as the column stores them. */
      downPayment: number | null;
      termMonths: number | null;
      apr: number | null;
    };
    /** `company_settings.financing_term_months` / `financing_apr`. */
    companyTerms?: { termMonths: number | null; apr: number | null };
    /**
     * Save one of the three. Null clears it back to the company's default —
     * emptying a box is an answer, not a zero.
     */
    onSaveTerms?: (
      patch: Partial<{
        financing_down_payment: number | null;
        financing_term_months: number | null;
        financing_apr: number | null;
      }>,
    ) => void;
  };
}) {
  const [draft, setDraft] = useState<Draft>({});

  // What the schedule looks like right now, drafts included. Recomputed on
  // every render, which is what makes the Final live.
  const live = paymentSchedule(
    total,
    payments.map((p) =>
      p.slot in draft ? { ...p, amount: parseDraft(draft[p.slot]!) } : p,
    ),
  );

  /**
   * A box being typed into never disappears out from under the cursor.
   *
   * `live.rows` is derived from the draft, so emptying a leftover amount on a
   * sub-$1,000 job drops that row from the schedule immediately — unmounting
   * the input mid-edit, so blur never fires and the amount is never actually
   * cleared in the database. The rep watches the row vanish and the stored
   * figure survives. So: any slot with a draft open stays on screen until it
   * commits.
   */
  const rows = [
    ...live.rows,
    ...Object.keys(draft)
      .map(Number)
      .filter(
        (slot) =>
          slot !== FINAL_SLOT && !live.rows.some((row) => row.slot === slot),
      )
      .map((slot) => ({
        slot,
        label: PAYMENT_SLOT_LABEL[slot] ?? t("Payment {n}", { n: slot }),
        amount: cents(0),
        triggerText: payments.find((p) => p.slot === slot)?.trigger_text ?? "",
        editable: true,
        isDefault: false,
      })),
  ].sort((a, b) => a.slot - b.slot);

  const rowFor = (slot: number) => payments.find((p) => p.slot === slot);

  const clearDraft = (slot: number) =>
    setDraft((d) => {
      const next = { ...d };
      delete next[slot];
      return next;
    });

  const save = (slot: number, raw: string) => {
    const row = rowFor(slot);
    if (!row) return clearDraft(slot);

    const typed = parseDraft(raw);

    // A schedule that does not add up is not written. The rep fixes it first —
    // so the draft STAYS on screen along with the error explaining it. Clearing
    // it here would snap the box back to the stored number and take the error
    // with it, which reads as "saved" for a figure that was thrown away.
    const attempted = paymentSchedule(
      total,
      payments.map((p) => (p.slot === slot ? { ...p, amount: typed } : p)),
    );
    if (attempted.error) return;

    clearDraft(slot);

    // Tabbing through a box is not an edit. Without this, every focus/blur
    // fires an UPDATE and a full reload of the estimate for an unchanged value.
    const stored = row.amount == null ? null : Number(row.amount);
    if (typed === stored) return;

    onSaveAmount(row.id, typed);
  };

  /*
   * The terms this estimate will actually be presented with.
   *
   * The estimate answers first and the company answers for anything it left
   * null — the same fall-through the Show/Hide select above it uses, and the
   * same one `PresentEstimateModal` hands to the document. A rep who never
   * opens this row gets the company's programme; one who types a rate for a
   * particular customer gets theirs.
   */
  const effectiveTerms: FinancingTerms | null = financing
    ? {
        downPayment:
          financing.terms?.downPayment != null
            ? toCents(financing.terms.downPayment)
            : null,
        termMonths:
          financing.terms?.termMonths ??
          financing.companyTerms?.termMonths ??
          null,
        apr: financing.terms?.apr ?? financing.companyTerms?.apr ?? null,
      }
    : null;

  const money = financingBlock(total, null, effectiveTerms);

  return (
    <>
      <div className="chk-sub paysched">{t("Payment Schedule")}</div>
      <div className="paysched">
        {rows.map((row) => {
          const stored = rowFor(row.slot);
          const shown =
            row.slot in draft ? draft[row.slot]! : formatCents(row.amount);

          return (
            <div className="rrow" key={row.slot}>
              <span className="k">
                {t(row.label)}
                {/* Only the first slot has a default worth announcing — the
                    other two default to nothing, which needs no label. */}
                {row.isDefault && row.slot === 1 ? (
                  <span className="n"> {t("50% default")}</span>
                ) : null}
              </span>

              {row.editable ? (
                <input
                  className="num"
                  aria-label={t(row.label)}
                  value={shown}
                  onChange={(e) => {
                    // Anything that is not money is not a keystroke. See
                    // MONEYISH above for why this is a filter, not a parse.
                    if (!MONEYISH.test(e.target.value)) return;
                    setDraft((d) => ({ ...d, [row.slot]: e.target.value }));
                  }}
                  onFocus={() =>
                    setDraft((d) => ({
                      ...d,
                      [row.slot]:
                        stored?.amount == null ? "" : String(stored.amount),
                    }))
                  }
                  onBlur={(e) => save(row.slot, e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter")
                      (e.target as HTMLInputElement).blur();
                  }}
                />
              ) : (
                // Never an input. The Final is the remainder, and a box invites
                // somebody to type a number that stops being true. A negative
                // remainder is not money, so it shows as nothing at all — the
                // error below says by how much and what to do about it.
                <span className="num" aria-label={t(row.label)}>
                  <b>{row.amount < 0 ? "—" : formatCents(row.amount)}</b>
                </span>
              )}

              <TriggerCell
                value={row.triggerText}
                onSave={(v) => {
                  if (stored) onSaveTrigger(stored.id, v || null);
                }}
              />
            </div>
          );
        })}
      </div>

      {live.error ? (
        <div className="err paysched" role="alert">
          {t(live.error)}
        </div>
      ) : null}

      {!live.split && total > 0 ? (
        <div className="n paysched">
          {t("Under $1,000 — no deposit, nothing to collect at signing.")}
        </div>
      ) : null}

      {plan && total > 0 && !live.error ? (
        <PaymentPlanRows plan={plan} final={live.final} />
      ) : null}

      {/* The whole block goes while the company may not offer financing at
          all — off since 2026-09-08, waiting on Foundation Finance. Showing a
          monthly figure and a Show/Hide choice that changes nothing would be a
          control that lies; the switch is in Tools, said once, where it can be
          turned back on. */}
      {financing?.companyOffers === false ? (
        <div className="paysched">
          <div className="chk-sub">{t("Financing")}</div>
          <div className="n">
            {t(
              "Financing is off for every job until Foundation Finance approves it. Turn it back on in Tools → Integrations.",
            )}
          </div>
        </div>
      ) : financing && money ? (
        <div className="paysched">
          <div className="chk-sub">{t("Financing")}</div>
          <div className="rrow">
            {/* "As low as" is the $13-per-$1,000 illustration talking. Once a
                rate and a term are set the figure is the payment on this job,
                to the cent, and calling it "as low as" would undersell the one
                number the rep is about to read out. */}
            <span className="k">
              {money.quoted ? t("Monthly payment") : t("Payments as low as")}
            </span>
            <span className="num">
              <b>{money.monthlyFormatted}</b>
              {t("/month")}
            </span>
            {/* Two answers, not three. "Company default" was the third and it
                was the one nobody wanted: it reads as an answer rather than as
                what happens.

                So an estimate that has never been asked — show_financing null —
                is shown as whatever it will actually do, which is `showFinancing`
                falling through to the company setting. Reading it as Show
                regardless would be a lie the moment somebody sets
                show_financing_default to false: the card would say Show and the
                customer's copy would carry no monthly payment. This is the same
                function the presented document is built with, so the two cannot
                disagree. */}
            <select
              aria-label={t("Show financing")}
              value={
                showFinancing(
                  financing.show,
                  financing.companyDefault,
                  financing.jobType,
                  financing.companyOffers,
                )
                  ? "yes"
                  : "no"
              }
              onChange={(e) => financing.onChange(e.target.value === "yes")}
            >
              <option value="yes">{t("Show on estimate")}</option>
              <option value="no">{t("Hide on estimate")}</option>
            </select>
          </div>
          {financing.onSaveTerms ? (
            <FinancingTermsRows
              terms={financing.terms}
              companyTerms={financing.companyTerms}
              onSave={financing.onSaveTerms}
              quoted={money.quoted}
            />
          ) : null}
        </div>
      ) : null}
    </>
  );
}
